<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ScenariosAPI from 'dashboard/api/scenarios';
import en from './en.json';
import ru from './ru.json';

const props = defineProps({
  scenario: { type: Object, required: true },
  dirty: { type: Boolean, default: false },
  busy: { type: Boolean, default: false },
});
const emit = defineEmits(['action']);
const { t, locale } = useI18n({ useScope: 'local', messages: { en, ru } });
const historyDialog = ref(null);
const confirmDialog = ref(null);
const rows = ref([]);
const loading = ref(false);
const error = ref('');
const command = ref('publish');
const version = ref(null);
const supported = computed(() =>
  Number.isInteger(props.scenario.editor_revision)
);
const locked = computed(() => props.dirty || props.busy || !supported.value);
const confirmTitle = computed(() => t(`versions.confirm_${command.value}`));
const confirmHint = computed(() =>
  t(`versions.hint_${command.value}`, {
    version: version.value || props.scenario.published_version,
  })
);
const dateLabel = value =>
  new Intl.DateTimeFormat(locale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

const openHistory = async () => {
  historyDialog.value.open();
  loading.value = true;
  error.value = '';
  try {
    rows.value = (await ScenariosAPI.versions(props.scenario.id)).data;
  } catch {
    error.value = t('versions.loadFailed');
  } finally {
    loading.value = false;
  }
};
const request = (action, number = null) => {
  command.value = action;
  version.value = number;
  historyDialog.value.close();
  confirmDialog.value.open();
};
const confirm = () => {
  emit('action', { action: command.value, version: version.value });
  confirmDialog.value.close();
};
</script>

<template>
  <div class="flex items-center gap-1 border-s border-n-weak ps-2">
    <Button
      icon="i-lucide-history"
      :label="
        scenario.published_version
          ? t('versions.number', { number: scenario.published_version })
          : t('versions.history')
      "
      :aria-label="t('versions.history')"
      :title="t('versions.history')"
      variant="ghost"
      color="slate"
      :disabled="busy || !supported"
      @click="openHistory"
    />
    <Button
      :icon="scenario.status === 'active' ? 'i-lucide-pause' : 'i-lucide-play'"
      :aria-label="
        t(scenario.status === 'active' ? 'versions.pause' : 'versions.activate')
      "
      :title="
        t(scenario.status === 'active' ? 'versions.pause' : 'versions.activate')
      "
      variant="ghost"
      color="slate"
      :disabled="busy || !supported || !scenario.published_version"
      @click="request(scenario.status === 'active' ? 'pause' : 'activate')"
    />
    <Button
      icon="i-lucide-upload"
      :label="t('versions.publish')"
      :aria-label="t('versions.publish')"
      :title="dirty ? t('versions.saveFirst') : t('versions.publish')"
      :variant="scenario.has_draft && !dirty ? 'solid' : 'ghost'"
      :disabled="locked || !scenario.has_draft"
      class="max-sm:[&>span.truncate]:hidden"
      @click="request('publish')"
    />
  </div>
  <Dialog
    ref="historyDialog"
    :title="t('versions.history')"
    :cancel-button-label="t('editor.closePanel')"
    :show-confirm-button="false"
  >
    <p class="text-sm text-n-slate-11">{{ t('versions.historyHint') }}</p>
    <p v-if="loading" role="status">{{ t('loading') }}</p>
    <p v-else-if="error" role="alert" class="text-n-ruby-11">{{ error }}</p>
    <p v-else-if="!rows.length" class="text-sm text-n-slate-11">
      {{ t('versions.empty') }}
    </p>
    <ol
      v-else
      class="m-0 p-0 list-none max-h-[50vh] overflow-y-auto divide-y divide-n-weak"
    >
      <li
        v-for="row in rows"
        :key="row.number"
        class="flex items-center gap-3 py-3"
      >
        <div class="min-w-0 flex-1">
          <p class="m-0 text-sm font-medium">
            {{ t('versions.number', { number: row.number }) }} · {{ row.name }}
          </p>
          <p class="m-0 mt-1 text-xs text-n-slate-11">
            {{ dateLabel(row.created_at) }} ·
            {{ row.author || t('versions.baselineAuthor') }}
          </p>
          <p
            class="m-0 mt-1 text-xs"
            :class="row.templates_frozen ? 'text-n-teal-11' : 'text-n-amber-11'"
          >
            {{
              row.templates_frozen
                ? t('versions.templatesFrozen', {
                    count: row.template_snapshots.length,
                  })
                : t('versions.templatesLegacy')
            }}
          </p>
        </div>
        <Button
          :label="t('versions.restore')"
          :aria-label="t('versions.restoreNumber', { number: row.number })"
          variant="ghost"
          size="sm"
          :disabled="locked"
          @click="request('restoreVersion', row.number)"
        />
      </li>
    </ol>
    <div
      v-if="scenario.has_draft && scenario.published_version"
      class="mt-4 pt-4 border-t border-n-weak"
    >
      <Button
        :label="t('versions.discard')"
        variant="ghost"
        color="ruby"
        :disabled="locked"
        @click="request('discardDraft')"
      />
    </div>
    <p v-if="dirty" class="mt-4 text-xs text-n-amber-11">
      {{ t('versions.saveFirst') }}
    </p>
  </Dialog>
  <Dialog
    ref="confirmDialog"
    :title="confirmTitle"
    :confirm-button-label="t('versions.continue')"
    :cancel-button-label="t('cancel')"
    @confirm="confirm"
  >
    <p class="text-sm text-n-slate-12">{{ confirmHint }}</p>
    <p v-if="command === 'publish'" class="text-sm text-n-slate-11">
      {{
        t(
          scenario.status === 'active'
            ? 'versions.activeEffect'
            : 'versions.pausedEffect'
        )
      }}
    </p>
    <p v-if="command === 'publish'" class="text-xs text-n-slate-11">
      {{ t('versions.templateScope') }}
    </p>
  </Dialog>
</template>
